import { Box, Button } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AutocompleteDropdown } from 'src/shared/components/form/autocompleteDropdown';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { SelectableItem } from 'src/shared/models/gui/selectableItem';
import { getAllProfileGroups, getProfileGroupByIdAllItems } from 'src/shared/services/profileGroupService';
import { ProfileGroup } from '../models/profileGroup';

type MemberAdvancedSearchAddProfileType = {
  propName: string;
  callback: (newProfiles: any[]) => void; // Pass new profiles to the parent
  closeEdit: () => void; // Close the editor
};

export const MemberAdvancedSearchAddProfile = (props: MemberAdvancedSearchAddProfileType) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { propName, callback, closeEdit } = props;
  const { t } = useTranslation(['member', 'common']);
  const [loadingProfileGroup, setLoadingProfileGroup] = useState(false);
  const [profileGroups, setProfileGroups] = useState<ProfileGroup[]>([]);
  const [selectedProfileGroup, setSelectedProfileGroup] = useState<ProfileGroup>();
  const [profileGroupWithItems, setProfileGroupWithItems] = useState<ProfileGroup>();
  const [selectedProfileItem, setSelectedProfileItem] = useState<SelectableItem>();

  const isMounted = useIsMounted();

  // Fetch profile groups on mount
  useEffect(() => {
    const fetchProfileGroups = async () => {
      try {
        const res = await getAllProfileGroups(true, true); // Fetch all profile groups
        if (isMounted()) setProfileGroups(res.data.results);
      } catch (error) {
        if (isMounted()) ConsoleHelper.log(error);
      }
    };
    fetchProfileGroups();
  }, [isMounted]);

  // Fetch items of the selected profile group
  useEffect(() => {
    if (!selectedProfileGroup) return;

    const fetchProfileGroupItems = async () => {
      setLoadingProfileGroup(true);
      try {
        const res = await getProfileGroupByIdAllItems(selectedProfileGroup.memberProfile_id, true); // Fetch profile group items
        if (isMounted()) setProfileGroupWithItems(res.data);
      } catch (error) {
        if (isMounted()) ConsoleHelper.log(error);
      } finally {
        if (isMounted()) setLoadingProfileGroup(false);
      }
    };

    fetchProfileGroupItems();
  }, [selectedProfileGroup, isMounted]);

  const handleSaveProfile = () => {
    if (selectedProfileItem) {
      callback([
        {
          memberProfileItem_id: +selectedProfileItem.value,
          groupName: profileGroupWithItems?.title,
          itemName: selectedProfileItem.label,
        },
      ]);
    } else if (profileGroupWithItems) {
      const newProfiles = profileGroupWithItems.profileItems.map((item) => ({
        memberProfileItem_id: item.memberProfileItem_id,
        groupName: profileGroupWithItems.title,
        itemName: item.name,
      }));
      callback(newProfiles); // Pass the new profiles to the parent
      // closeEdit(); // Close the editor
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      {/* Profile group selection */}
      <Box sx={{ mb: 2 }}>
        <AutocompleteDropdown<SelectableItem>
          options={profileGroups.map((group) => ({
            label: group.title,
            value: group.memberProfile_id.toString(),
          }))}
          name='profileGroup'
          nameProp='label'
          keyProp='value'
          callback={(val) => {
            const selectedGroup = profileGroups.find((group) => group.memberProfile_id.toString() === val?.value);
            setSelectedProfileGroup(selectedGroup || undefined);
            setSelectedProfileItem(undefined);
          }}
          label={t('member:editMember.profileGroup')}
          componentWidth='100%'
        />
      </Box>

      {/* Profile items selection */}
      {profileGroupWithItems && !loadingProfileGroup && (
        <Box sx={{ mb: 2 }}>
          <AutocompleteDropdown<SelectableItem>
            options={profileGroupWithItems.profileItems.map((item) => ({
              label: item.name,
              value: item.memberProfileItem_id.toString(),
            }))}
            name='profileItem'
            nameProp='label'
            keyProp='value'
            callback={(val) => {
              if (val) setSelectedProfileItem(val);
              else setSelectedProfileItem(undefined);
            }}
            label={t('member:editMember.profile')}
            componentWidth='100%'
          />
        </Box>
      )}

      {/* Action buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant='outlined' size='small' onClick={closeEdit}>
          {t('common:button.cancel')}
        </Button>
        <Button
          variant='contained'
          disableElevation
          size='small'
          sx={{ ml: 1 }}
          disabled={!profileGroupWithItems || loadingProfileGroup}
          onClick={handleSaveProfile}
        >
          {t('common:button.choose')}
        </Button>
      </Box>
    </Box>
  );
};
